import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Calendar as CalendarIcon, Info, Users, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { studentService } from '../../services/studentService';

// Premium Color Palette
const COLORS = {
    present: 'text-green-500 bg-green-50 border-green-100',
    absent: 'text-red-500 bg-red-50 border-red-100',
    holiday: 'text-gray-400 bg-gray-50 border-gray-100',
    today: 'ring-2 ring-pragyaBlue ring-offset-2',
    selected: 'bg-pragyaBlue text-white shadow-lg shadow-pragyaBlue/30',
};

const Attendance = () => {
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [student, setStudent] = useState(null);
    const [loading, setLoading] = useState(true);

    // Dummy data - structured to be easily replaced by API
    const [attendanceRecords, setAttendanceRecords] = useState({
        // February 2026 (Selected Month)
        '2026-02-01': 'present',
        '2026-02-02': 'present',
        '2026-02-03': 'absent',
        '2026-02-04': 'present',
        '2026-02-05': 'present',
        '2026-02-06': 'absent',
        '2026-02-09': 'present',
        '2026-02-10': 'present',

        // January 2026
        '2026-01-01': 'present',
        '2026-01-02': 'present',
        '2026-01-05': 'absent',
        '2026-01-06': 'present',
        '2026-01-07': 'present',
        '2026-01-08': 'present',
        '2026-01-09': 'absent',
        '2026-01-12': 'present',
        '2026-01-13': 'present',
        '2026-01-14': 'present',
        '2026-01-15': 'absent',
        '2026-01-16': 'present',
        '2026-01-19': 'present',
        '2026-01-20': 'present',
        '2026-01-21': 'present',
        '2026-01-22': 'absent',
        '2026-01-23': 'present',
        '2026-01-27': 'present',
        '2026-01-28': 'present',
        '2026-01-29': 'present',
        '2026-01-30': 'present',
        '2026-01-31': 'present',
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchStudentData();
    }, []);

    const fetchStudentData = async () => {
        try {
            setLoading(true);
            const studentId = localStorage.getItem("selectedStudentId");
            if (!studentId) {
                toast.error("No student selected");
                navigate('/dashboard');
                return;
            }

            const response = await studentService.getStudentEnrollment(studentId);
            const enrollmentData = Array.isArray(response.data) ? response.data[0] : response.data;

            if (enrollmentData) {
                setStudent({
                    name: enrollmentData.student?.name || enrollmentData.student?.firstName || "N/A",
                    class: enrollmentData.class?.name || "N/A",
                    section: enrollmentData.section || "A",
                    rollNo: enrollmentData?.rollno || "N/A",
                    imageUrl: enrollmentData.student?.profilePicture || "/un.png",
                });
            }
        } catch (error) {
            console.error("Error fetching student profile:", error);
            toast.error("Failed to load student profile");
        } finally {
            setLoading(false);
        }
    };

    const daysInMonth = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        return new Date(year, month + 1, 0).getDate();
    }, [currentDate]);

    const firstDayOfMonth = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        return new Date(year, month, 1).getDay();
    }, [currentDate]);

    const monthName = currentDate.toLocaleString('default', { month: 'long' });
    const year = currentDate.getFullYear();

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const isToday = (day) => {
        const today = new Date();
        return day === today.getDate() &&
            currentDate.getMonth() === today.getMonth() &&
            currentDate.getFullYear() === today.getFullYear();
    };

    const getAttendanceStatus = (day) => {
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return attendanceRecords[dateStr];
    };

    const stats = useMemo(() => {
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // getMonth() is 0-indexed

        // Filter records that match current year and month
        const monthlyRecords = Object.entries(attendanceRecords).filter(([date]) => {
            const [year, month] = date.split('-').map(Number);
            return year === currentYear && month === currentMonth;
        });

        const present = monthlyRecords.filter(([_, status]) => status === 'present').length;
        const absent = monthlyRecords.filter(([_, status]) => status === 'absent').length;

        return { present, absent, total: present + absent };
    }, [attendanceRecords, currentDate]);

    const calendarDays = [];
    // Padding for first day of month
    for (let i = 0; i < firstDayOfMonth; i++) {
        calendarDays.push(<div key={`empty-${i}`} className="h-14 sm:h-20" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
        const status = getAttendanceStatus(day);
        calendarDays.push(
            <motion.div
                key={day}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2, delay: day * 0.01 }}
                className={`relative h-14 sm:h-20 border border-slate-100 rounded-xl flex flex-col items-center justify-center gap-1 transition-all duration-300 bg-white hover:shadow-md ${isToday(day) ? COLORS.today : ''}`}
            >
                <span className={`text-sm font-semibold ${isToday(day) ? 'text-pragyaBlue' : 'text-slate-600'}`}>{day}</span>
                {status === 'present' && (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="flex items-center justify-center p-1 rounded-full bg-green-500/10"
                    >
                        <Check size={14} className="text-green-500 stroke-[3]" />
                    </motion.div>
                )}
                {status === 'absent' && (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="flex items-center justify-center p-1 rounded-full bg-red-500/10"
                    >
                        <Check size={14} className="text-red-500 stroke-[3]" />
                    </motion.div>
                )}
            </motion.div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
                <div className="w-16 h-16 border-4 border-pragyaBlue/20 border-t-pragyaBlue rounded-full animate-spin mb-4" />
                <p className="text-slate-500 animate-pulse">Loading Attendance...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-32">
            {/* Header Section */}
            <div className="bg-white px-6 pt-6 pb-10 rounded-b-[2.5rem] shadow-sm border-b border-slate-100">
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 rounded-full hover:bg-slate-50 transition-colors"
                    >
                        <ChevronLeft size={24} className="text-slate-600" />
                    </button>
                    <h2 className="text-lg font-bold text-slate-800">Attendance Report</h2>
                    <div className="w-10" /> {/* Spacer */}
                </div>

                <div className="flex items-center gap-4 mb-6">
                    <div className="relative">
                        <img
                            src={student?.imageUrl || "/un.png"}
                            alt="Profile"
                            className="w-16 h-16 rounded-2xl object-cover border-2 border-pragyaBlue/20"
                        />
                        <div className="absolute -bottom-2 -right-2 bg-green-500 w-5 h-5 rounded-full border-2 border-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">{student?.name}</h1>
                        <p className="text-slate-500 text-sm font-medium">Class: {student?.class} | Section: {student?.section}</p>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-green-50 p-4 rounded-2xl border border-green-100 text-center">
                        <p className="text-xs text-green-600 font-bold uppercase tracking-wider mb-1">Present</p>
                        <p className="text-2xl font-black text-green-700">{stats.present}</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-2xl border border-red-100 text-center">
                        <p className="text-xs text-red-600 font-bold uppercase tracking-wider mb-1">Absent</p>
                        <p className="text-2xl font-black text-red-700">{stats.absent}</p>
                    </div>
                    <div className="bg-pragyaBlue/5 p-4 rounded-2xl border border-pragyaBlue/10 text-center">
                        <p className="text-xs text-pragyaBlue font-bold uppercase tracking-wider mb-1">Working</p>
                        <p className="text-2xl font-black text-pragyaBlue">{daysInMonth}</p>
                    </div>
                </div>
            </div>

            {/* Calendar Controls */}
            <div className="px-4 -mt-6">
                <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-4 border border-slate-100">
                    <div className="flex items-center justify-between mb-6 px-2">
                        <div className="flex items-center gap-3">
                            <div className="bg-pragyaBlue/10 p-2 rounded-xl text-pragyaBlue">
                                <CalendarIcon size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-800 text-lg leading-none">{monthName}</h3>
                                <p className="text-slate-400 text-xs font-medium mt-1">{year}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handlePrevMonth}
                                className="p-2.5 rounded-xl hover:bg-slate-50 border border-slate-100 text-slate-600 transition-colors"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <button
                                onClick={handleNextMonth}
                                className="p-2.5 rounded-xl hover:bg-slate-50 border border-slate-100 text-slate-600 transition-colors"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Week Days Header */}
                    <div className="grid grid-cols-7 mb-2 text-center">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                            <span key={day + i} className="text-[10px] font-black text-slate-300 uppercase tracking-widest py-2">
                                {day}
                            </span>
                        ))}
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-2">
                        {calendarDays}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-4 px-2 pt-6 border-t border-slate-50">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500" />
                            <span className="text-xs font-semibold text-slate-500">Present</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <span className="text-xs font-semibold text-slate-500">Absent</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-pragyaBlue" />
                            <span className="text-xs font-semibold text-slate-500">Today</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Attendance Analytics (Optional Placeholder) */}
            <div className="px-4 mt-6">
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-orange-50 rounded-xl text-orange-500">
                            <Info size={18} />
                        </div>
                        <h4 className="font-bold text-slate-800">Quick Summary</h4>
                    </div>
                    <p className="text-slate-500 text-sm leading-relaxed">
                        Attendance rate for {monthName} is <span className="text-green-600 font-bold">{stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0}%</span>.
                        Maintain at least 85% attendance for examination eligibility.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Attendance;
